import * as React from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { ArrowDown, ArrowLeft, ArrowUp, ChevronRight, Pencil, Plus, RotateCcw, Sparkles, Trash2 } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { BlueprintCheck } from "@/components/exams/BlueprintCheck"
import { ExamFormDialog } from "@/components/exams/ExamFormDialog"
import { caseCount, duration } from "@/components/exams/format"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { questionCount } from "@/components/quizzes/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Radio, RadioGroup } from "@/components/ui/radio"
import type { PageProps } from "@/types/inertia"
import type { Blueprint, BuilderCase, ExamSummary } from "@/types/exams"

const POLL_MS = 3000

export default function AdminPracticeExamShow({
  exam,
  halves,
  blueprint,
  publish_blockers,
  cases,
  exam_versions,
  question_counts,
  ai_configured,
}: {
  exam: ExamSummary & { exam_version_id: number }
  halves: number[]
  blueprint: Blueprint
  publish_blockers: string[]
  cases: BuilderCase[]
  exam_versions: { id: number; name: string }[]
  question_counts: number[]
  ai_configured: boolean
}) {
  const [editing, setEditing] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [adding, setAdding] = React.useState(false)
  const [drafting, setDrafting] = React.useState(false)
  const basePath = `/admin/practice_exams/${exam.id}`
  const published = exam.status === "published"
  const anyDrafting = cases.some((c) => c.drafting_status === "drafting")

  React.useEffect(() => {
    if (!anyDrafting) return
    const timer = window.setInterval(() => router.reload({ only: ["cases", "blueprint", "publish_blockers", "exam", "halves"] }), POLL_MS)
    return () => window.clearInterval(timer)
  }, [anyDrafting])

  const description = `Build the case studies for ${exam.title}, check its blueprint balance, and publish it.`

  return (
    <>
      <Head title={`${exam.title} (practice exam)`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${exam.title} (practice exam)`} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <Link href="/admin/practice_exams" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> Practice exams
        </Link>
        <PageHeader
          title={exam.title}
          description={
            <>
              <Badge tone={published ? "accent" : "muted"} className="mr-2">
                {published ? "Published" : "Draft"}
              </Badge>
              {exam.exam_version_name} · {caseCount(exam.case_count)} · {questionCount(exam.question_count)} ·{" "}
              {duration(exam.time_limit_minutes * 60)}
            </>
          }
          actions={
            <>
              <Button type="button" variant="ghost" size="icon" aria-label="Edit exam" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" aria-label="Delete exam" onClick={() => setDeleting(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
              {published ? (
                <Button type="button" variant="secondary" onClick={() => router.patch(`${basePath}/unpublish`)}>
                  Unpublish
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={publish_blockers.length > 0}
                  title={publish_blockers[0]}
                  onClick={() => router.patch(`${basePath}/publish`)}
                >
                  Publish
                </Button>
              )}
            </>
          }
        />

        {!published && publish_blockers.length > 0 && (
          <div className="callout callout-signal mt-6">
            <p>
              <strong>Before publishing:</strong>
            </p>
            <ul className="mt-1">
              {publish_blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        )}
        {published && (
          <p className="mt-4 text-sm text-ink-muted">
            Edits apply to users right away. Scores from finished attempts don't change.
          </p>
        )}

        <section className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2>Case studies</h2>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" /> Add case
              </Button>
              <Button
                type="button"
                onClick={() => setDrafting(true)}
                disabled={!ai_configured}
                title={ai_configured ? undefined : "Add an Anthropic API key to draft with AI"}
              >
                <Sparkles className="h-4 w-4" /> Draft a case with AI
              </Button>
            </div>
          </div>
          {halves.length === 2 && (
            <p className="mt-2 text-sm text-ink-muted">
              Part 1: {caseRange(1, halves[0])} · break · Part 2: {caseRange(halves[0] + 1, halves[0] + halves[1])}
            </p>
          )}
          {cases.length === 0 ? (
            <div className="callout mt-4">
              <p>No cases yet. Add one by hand or draft one with AI.</p>
            </div>
          ) : (
            <ol className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page p-0">
              {cases.map((c, index) => (
                <CaseRow key={c.id} item={c} index={index} last={index === cases.length - 1} />
              ))}
            </ol>
          )}
        </section>

        <section className="mt-10">
          <h2>Blueprint check</h2>
          <p className="mt-1">How this exam's questions spread across {exam.exam_version_name}'s content areas.</p>
          <BlueprintCheck blueprint={blueprint} />
        </section>

        <ExamFormDialog
          open={editing}
          onOpenChange={setEditing}
          action={basePath}
          method="patch"
          title="Edit exam"
          submitLabel="Save"
          examVersions={exam_versions}
          initial={{
            title: exam.title,
            exam_version_id: String(exam.exam_version_id),
            instructions: exam.instructions ?? "",
            time_limit_minutes: String(exam.time_limit_minutes),
          }}
        />
        <ConfirmDialog
          open={deleting}
          onOpenChange={setDeleting}
          title="Delete this exam?"
          description={`"${exam.title}", its cases, and everyone's attempts on it will be deleted. This can't be undone.`}
          confirmLabel="Delete exam"
          onConfirm={() => router.delete(basePath)}
        />
        <AddCaseDialog open={adding} onOpenChange={setAdding} action={`${basePath}/case_studies`} />
        <DraftCaseDialog
          open={drafting}
          onOpenChange={setDrafting}
          action={`${basePath}/case_studies/draft`}
          questionCounts={question_counts}
        />
      </AdminShell>
    </>
  )
}

function CaseRow({ item, index, last }: { item: BuilderCase; index: number; last: boolean }) {
  const path = `/admin/case_studies/${item.id}`
  const move = (direction: "up" | "down") => router.patch(`${path}/move`, { direction }, { preserveScroll: true })
  const counts = item.section_counts

  return (
    <li className="flex list-none items-center gap-3 px-4 py-3">
      <span className="w-6 shrink-0 text-right text-xs text-ink-muted">{index + 1}</span>
      {item.drafting_status ? (
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-ink-display">{item.title}</div>
          <div className="truncate text-xs text-ink-muted">
            {item.drafting_status === "drafting" ? "AI is drafting this case…" : item.drafting_error}
          </div>
        </div>
      ) : (
        <Link href={path} className="flex min-w-0 flex-1 items-center gap-3 no-underline">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-ink-display">{item.title}</div>
            <div className="truncate text-xs text-ink-muted">
              {questionCount(item.question_count)} (intake {counts.intake} · session 1 {counts.session_1} · session 2{" "}
              {counts.session_2}){item.presenting_concern ? ` · ${item.presenting_concern}` : ""}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
        </Link>
      )}
      {item.ai_draft && <Badge tone="signal">AI draft</Badge>}
      {item.drafting_status === "drafting" && <Badge tone="muted">Drafting…</Badge>}
      {item.drafting_status === "failed" && (
        <>
          <Badge tone="muted">Failed</Badge>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Retry drafting"
            onClick={() => router.post(`${path}/retry_draft`, {}, { preserveScroll: true })}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" aria-label="Delete failed draft" onClick={() => router.delete(path)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      )}
      <div className="flex shrink-0 items-center">
        <Button type="button" variant="ghost" size="icon" aria-label={`Move case ${index + 1} up`} disabled={index === 0} onClick={() => move("up")}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" aria-label={`Move case ${index + 1} down`} disabled={last} onClick={() => move("down")}>
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>
    </li>
  )
}

function AddCaseDialog({ open, onOpenChange, action }: { open: boolean; onOpenChange: (o: boolean) => void; action: string }) {
  const form = useForm({ title: "", presenting_concern: "" })
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(action, { onSuccess: () => onOpenChange(false) })
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add a case study</DialogTitle>
            <DialogDescription>You'll write the intake, sessions, and questions next.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <label htmlFor="case-title">Title</label>
              <Input id="case-title" required autoFocus value={form.data.title} onChange={(e) => form.setData("title", e.target.value)} />
            </div>
            <div className="space-y-2">
              <label htmlFor="case-concern">Presenting concern (optional)</label>
              <Input
                id="case-concern"
                placeholder="e.g. Adolescent with generalized anxiety"
                value={form.data.presenting_concern}
                onChange={(e) => form.setData("presenting_concern", e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.processing}>
              Add case
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DraftCaseDialog({
  open,
  onOpenChange,
  action,
  questionCounts,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  action: string
  questionCounts: number[]
}) {
  const { props } = usePage<PageProps>()
  const form = useForm({ presenting_concern: "", question_count: String(questionCounts[1] ?? questionCounts[0]) })
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(action, { onSuccess: () => { onOpenChange(false); form.reset() } })
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Draft a case with AI</DialogTitle>
            <DialogDescription>
              The AI writes the intake summary, both sessions, and content-area-tagged questions, leaning toward areas this
              exam is short on. It's marked "AI draft – needs review" until you review it.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <label htmlFor="draft-concern">Presenting concern</label>
              <textarea
                id="draft-concern"
                className="form-control form-control-textarea min-h-20"
                required
                placeholder="e.g. Adolescent with generalized anxiety and school refusal"
                value={form.data.presenting_concern}
                onChange={(e) => form.setData("presenting_concern", e.target.value)}
              />
              {props.errors?.presenting_concern && (
                <p className="text-xs text-danger-display">{props.errors.presenting_concern}</p>
              )}
            </div>
            <fieldset className="space-y-3">
              <legend>Questions</legend>
              <RadioGroup orientation="horizontal">
                {questionCounts.map((n) => (
                  <label key={n} className="flex items-center gap-2 font-normal text-ink-body">
                    <Radio
                      name="question_count"
                      checked={form.data.question_count === String(n)}
                      onChange={() => form.setData("question_count", String(n))}
                    />
                    {n}
                  </label>
                ))}
              </RadioGroup>
            </fieldset>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.processing}>
              <Sparkles className="h-4 w-4" /> Draft case
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function caseRange(first: number, last: number) {
  return first === last ? `case ${first}` : `cases ${first}–${last}`
}
